"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { z } from "zod";
import Link from "next/link";
import type { Dispatch } from "react";
import type { FunnelWizardState, FunnelTemplateId } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale } from "@/lib/funnel-planner/schema";
import { track } from "@/lib/posthog/events";
import { buildFunnelPlannerTrackingSummary, submitFunnelCrm, fetchFunnelPdf, triggerPdfDownload } from "@/lib/funnel-planner/submit";
import { funnelText } from "@/lib/funnel-planner/i18n";
import { localizedPath } from "@/lib/seo/url";
import { useWebMcpTool } from "@/lib/webmcp/react";
import {
  joinAuditTicketGroups,
  STEP_SEVEN_CONTACT_HANDOFF_FIELD_TICKETS,
  STEP_SEVEN_CTA_TICKETS,
  STEP_SEVEN_DOWNLOAD_REVIEW_SUPPORT_TICKETS,
  STEP_SEVEN_FIELD_INPUT_TICKETS,
  STEP_SEVEN_OFFER_ASSEMBLY_MINI_CTA_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

type Action = { type: "SET_CONTACT"; contact: FunnelWizardState["contact"] };

const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const NAME_MAX = 120;
const COMPANY_MAX = 160;
const DOWNLOAD_REVIEW_REVEAL_TICKET =
  "P2-754001-section-step-07-download-review-before-after-ai-answer-reveal-";
const DOWNLOAD_REVIEW_AUDIT_TICKET = joinAuditTicketGroups(
  DOWNLOAD_REVIEW_REVEAL_TICKET,
  STEP_SEVEN_DOWNLOAD_REVIEW_SUPPORT_TICKETS,
  STEP_SEVEN_FIELD_INPUT_TICKETS,
  STEP_SEVEN_CONTACT_HANDOFF_FIELD_TICKETS,
);
const STEP_SEVEN_CONTACT_CTA_TICKETS = Array.from(new Set([
  ...STEP_SEVEN_OFFER_ASSEMBLY_MINI_CTA_TICKETS,
  ...STEP_SEVEN_CTA_TICKETS,
]));
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";

const TEXT_INPUT_IDLE_STYLE = { outline: "2px solid transparent" };
const TEXT_INPUT_FOCUS_STYLE = {
  outline: "2px solid var(--color-primary, #2563eb)",
  outlineOffset: "2px",
};
export function ContactCaptureStep({
  state,
  dispatch,
  content,
  locale,
  onSuccess,
}: {
  state: FunnelWizardState;
  dispatch: Dispatch<Action>;
  content: FunnelPlannerContent;
  locale: AppLocale;
  onSuccess: (email: string, templateId: FunnelTemplateId) => void;
}) {
  const progressPhases = [
    funnelText(content, "contactProgressAssembling", "Assembling your funnel plan..."),
    funnelText(content, "contactProgressCalculating", "Calculating projections..."),
    funnelText(content, "contactProgressGenerating", "Generating your PDF..."),
    funnelText(content, "contactProgressAlmostReady", "Almost ready..."),
  ];
  const [email, setEmail] = useState(state.contact?.email ?? "");
  const [emailTouched, setEmailTouched] = useState(false);
  const [name, setName] = useState(state.contact?.name ?? "");
  const [company, setCompany] = useState(state.contact?.company ?? "");
  const [privacy, setPrivacy] = useState(state.contact?.privacyNoticeAccepted === true);
  const [marketing, setMarketing] = useState(state.contact?.marketingEmailOptIn === true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [progressPhase, setProgressPhase] = useState(0);
  const [submitFocused, setSubmitFocused] = useState(false);
  const [focusedField, setFocusedField] = useState<"email" | "name" | "company" | null>(null);
  const submittedRef = useRef(false);
  const trackedCrmAcceptanceIdsRef = useRef(new Set<string>());
  const startedAt = useRef<number | null>(null);
  const emailErrorRef = useRef<HTMLParagraphElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const privacyInputRef = useRef<HTMLInputElement>(null);
  const phaseTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const funnelHandoffSchema = useMemo(
    () => z.object({
      email: z.string().email().max(254).describe(content.emailLabel),
      name: z.string().max(NAME_MAX).describe(content.nameLabel).optional(),
      company: z.string().max(COMPANY_MAX).describe(content.companyLabel).optional(),
    }).strict(),
    [content.companyLabel, content.emailLabel, content.nameLabel],
  );

  useWebMcpTool({
    action: "request_funnel_handoff",
    instance: "funnel_contact",
    schema: funnelHandoffSchema,
    enabled: !submitting,
    execute: (input) => {
      setEmail(input.email);
      setName(input.name ?? "");
      setCompany(input.company ?? "");
      setEmailTouched(false);
      setErrors({});
      requestAnimationFrame(() => emailInputRef.current?.focus());
      return {
        status: "review_required",
        uiState: { preparedFields: ["email", "name", "company"] },
      };
    },
  });

  useEffect(() => {
    startedAt.current = Date.now();
    return () => {
      submittedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (submitting) {
      phaseTimerRef.current = setInterval(() => {
        setProgressPhase((p) => Math.min(p + 1, progressPhases.length - 1));
      }, 3500);
    } else {
      if (phaseTimerRef.current) {
        clearInterval(phaseTimerRef.current);
        phaseTimerRef.current = null;
      }
    }
    return () => {
      if (phaseTimerRef.current) {
        clearInterval(phaseTimerRef.current);
        phaseTimerRef.current = null;
      }
    };
  }, [submitting, progressPhases.length]);

  const validateEmail = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return funnelText(content, "contactEmailInvalid", "Please enter a valid email address (e.g. you@company.com)");
    if (!trimmed.match(EMAIL_REGEX)) return funnelText(content, "contactEmailInvalid", "Please enter a valid email address (e.g. you@company.com)");
    return "";
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    const emailErr = validateEmail(email);
    if (emailErr) errs.email = emailErr;
    if (!privacy) errs.privacy = content.privacyRequired;
    return errs;
  };

  const handleRetry = () => {
    setErrors({});
    submittedRef.current = false;
    setSubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittedRef.current || submitting) return;

    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      setEmailTouched(true);
      setTimeout(() => {
        if (errs.email) {
          emailInputRef.current?.focus();
        } else if (errs.privacy) {
          privacyInputRef.current?.focus();
        }
      }, 0);
      return;
    }
    setErrors({});

    if (!state.templateId) {
      setErrors({ form: content.errorPdfFailed });
      return;
    }

    const form = e.currentTarget as HTMLFormElement;
    const honeypot = new FormData(form).get("website");
    if (typeof honeypot === "string" && honeypot.trim().length > 0) {
      track("funnel_planner_submit_blocked", {
        template_id: state.templateId,
        locale,
        reason: "honeypot",
      });
      submittedRef.current = false;
      setSubmitting(false);
      return;
    }

    const contact: FunnelWizardState["contact"] = {
      email: trimmedEmail,
      name: name || undefined,
      company: company || undefined,
      privacyNoticeAccepted: true,
      marketingEmailOptIn: marketing,
    };

    dispatch({ type: "SET_CONTACT", contact });

    submittedRef.current = true;
    setProgressPhase(0);
    setSubmitting(true);

    const submittedState = { ...state, contact, step: 7 as const };
    const trackingSummary = buildFunnelPlannerTrackingSummary(submittedState);
    const submitStart = Date.now();
    const flowStartedAt = startedAt.current ?? submitStart;

    try {
      const acceptance = await submitFunnelCrm(submittedState, locale);
      if (!trackedCrmAcceptanceIdsRef.current.has(acceptance.idempotency_key)) {
        trackedCrmAcceptanceIdsRef.current.add(acceptance.idempotency_key);
        track("funnel_planner_crm_submitted", {
          template_id: state.templateId,
          locale,
          ...trackingSummary,
        });
      }

      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 30000);
      const blob: Blob = await fetchFunnelPdf(submittedState, locale, { signal: controller.signal }).finally(() => clearTimeout(tid));

      track("funnel_planner_pdf_downloaded", {
        template_id: state.templateId,
        locale,
        duration_ms: Date.now() - submitStart,
      });

      track("funnel_planner_submitted", {
        template_id: state.templateId,
        locale,
        ...trackingSummary,
        stage_count: submittedState.stages.length,
        total_time_ms: Date.now() - flowStartedAt,
      });

      triggerPdfDownload(blob, state.templateId ?? "funnel-plan");
      submittedRef.current = false;
      onSuccess(trimmedEmail, state.templateId);
    } catch {
      submittedRef.current = false;
      setSubmitting(false);
      setErrors({ form: content.errorPdfFailed });
    }
  };

  const emailReady = EMAIL_REGEX.test(email.trim());
  const downloadReviewReadyCount = [emailReady, privacy, Boolean(state.templateId)].filter(Boolean).length;
  const downloadReviewState =
    downloadReviewReadyCount >= 3 ? "complete_result" : downloadReviewReadyCount > 0 ? "partial" : "active_inprogress";

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="space-y-4"
      data-funnel-planner-download-review="true"
      data-audit-ticket={DOWNLOAD_REVIEW_AUDIT_TICKET}
      data-tier-one-family="before_after_section_reveal"
      data-gamification-current-state={downloadReviewState}
      data-gamification-reduced-motion-static="supported"
      data-gamification-supported-states={READINESS_SUPPORTED_STATES}
      data-readiness-ready-count={downloadReviewReadyCount}
      data-readiness-total-count={3}
      data-readiness-tier={downloadReviewReadyCount >= 3 ? "download_review_ready" : "download_review_draft"}
      data-blockers-bucket={downloadReviewReadyCount >= 3 ? "none" : downloadReviewReadyCount >= 1 ? "one_two" : "three_four"}
      data-gamification-count-up-real-numbers="true"
      data-gamification-real-number-source="download_review_ready_checks"
      data-gamification-real-number-count={downloadReviewReadyCount}
      data-gamification-real-number-total={3}
      data-persona-memory-ttl-days={30}
      data-analytics-component-family="lead_form"
      data-analytics-component-id="funnel-planner.contact-capture.form"
      data-analytics-experience-id="xp.funnel-planner.contact-capture.control"
      data-analytics-variant-key={state.templateId ?? "control"}
      data-webmcp-react-form="funnel_contact"
    >
      <span className="sr-only" role="status" aria-live="polite">
        {downloadReviewReadyCount >= 3
          ? funnelText(content, "gamificationDownloadReviewReady", "Download review ready")
          : funnelText(content, "gamificationDownloadReviewWaiting", "Download review waiting for contact consent")}
      </span>
      <div style={{ marginBottom: "0.5rem" }}>
        <h2 style={{ fontSize: "1rem", fontWeight: 700, color: "#e2e8f0", margin: 0 }}>
          {funnelText(content, "contactStepHeading", "Get your personalized funnel plan")}
        </h2>
        <p style={{ fontSize: "0.8125rem", color: "#94a3b8", marginTop: "0.25rem" }}>
          {funnelText(content, "contactStepProgress", "Step 7 of 7")}
        </p>
      </div>
      <div>
        <label htmlFor="fp-email" className="text-sm font-semibold" style={{ color: "#e2e8f0" }}>
          {content.emailLabel}{" "}
          <span aria-hidden="true" style={{ color: "#ef4444" }}>*</span>
          <span className="sr-only">({funnelText(content, "requiredLabel", "required")})</span>
        </label>
        <input
          ref={emailInputRef}
          id="fp-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailTouched && errors.email) {
              const err = validateEmail(e.target.value);
              setErrors((prev) => ({ ...prev, email: err || "" }));
            }
          }}
          onBlur={() => {
            setFocusedField(null);
            setEmailTouched(true);
            const err = validateEmail(email.trim());
            if (err) {
              setErrors((prev) => ({ ...prev, email: err }));
            } else {
              setErrors((prev) => {
                const next = { ...prev };
                delete next.email;
                return next;
              });
            }
          }}
          placeholder={content.emailPlaceholder}
          autoComplete="email"
          maxLength={254}
          required
          aria-required="true"
          disabled={submitting}
          aria-describedby={errors.email ? "fp-email-error" : undefined}
          onFocus={() => setFocusedField("email")}
          style={{
            marginTop: "0.25rem",
            width: "100%",
            borderRadius: "0.375rem",
            border: `1px solid ${errors.email ? "#ef4444" : "#1e293b"}`,
            backgroundColor: "var(--color-background, #0f172a)",
            padding: "0.5rem 0.75rem",
            fontSize: "0.875rem",
            ...(focusedField === "email" ? TEXT_INPUT_FOCUS_STYLE : TEXT_INPUT_IDLE_STYLE),
            color: "#e2e8f0",
          }}
        />
        <p
          id="fp-email-error"
          ref={emailErrorRef}
          role="alert"
          tabIndex={-1}
          style={{
            marginTop: "0.25rem",
            fontSize: "0.875rem",
            color: "#ef4444",
            display: errors.email ? "flex" : "none",
            alignItems: "center",
            gap: "0.25rem",
            paddingLeft: "0.5rem",
            borderLeft: "2px solid #ef4444",
            outline: "none",
            animation: errors.email ? "errorFadeIn 0.15s ease-out" : undefined,
          }}
        >
          {errors.email}
        </p>
      </div>

      <div>
        <label htmlFor="fp-name" className="text-sm font-medium" style={{ color: "#94a3b8" }}>
          {content.nameLabel}{" "}
          <span style={{ color: "#64748b", fontStyle: "italic" }}>({funnelText(content, "optionalLabel", "optional")})</span>
        </label>
        <input
          id="fp-name"
          name="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={content.namePlaceholder}
          autoComplete="name"
          maxLength={120}
          disabled={submitting}
          onFocus={() => setFocusedField("name")}
          onBlur={() => setFocusedField(null)}
          style={{
            marginTop: "0.25rem",
            width: "100%",
            borderRadius: "0.375rem",
            border: "1px solid #1e293b",
            backgroundColor: "var(--color-background, #0f172a)",
            padding: "0.5rem 0.75rem",
            fontSize: "0.875rem",
            ...(focusedField === "name" ? TEXT_INPUT_FOCUS_STYLE : TEXT_INPUT_IDLE_STYLE),
            color: "#e2e8f0",
          }}
        />
        <p aria-live="polite" style={{ marginTop: "0.125rem", textAlign: "right", fontSize: "0.75rem", color: name.length >= NAME_MAX - 10 ? "#ef4444" : "#94a3b8" }}>
          {funnelText(content, "contactNameCounter", "{count}/{max}", { count: name.length, max: NAME_MAX })}
        </p>
      </div>

      <div>
        <label htmlFor="fp-company" className="text-sm font-medium" style={{ color: "#94a3b8" }}>
          {content.companyLabel}{" "}
          <span style={{ color: "#64748b", fontStyle: "italic" }}>({funnelText(content, "optionalLabel", "optional")})</span>
        </label>
        <input
          id="fp-company"
          name="company"
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder={content.companyPlaceholder}
          autoComplete="organization"
          maxLength={160}
          disabled={submitting}
          onFocus={() => setFocusedField("company")}
          onBlur={() => setFocusedField(null)}
          style={{
            marginTop: "0.25rem",
            width: "100%",
            borderRadius: "0.375rem",
            border: "1px solid #1e293b",
            backgroundColor: "var(--color-background, #0f172a)",
            padding: "0.5rem 0.75rem",
            fontSize: "0.875rem",
            ...(focusedField === "company" ? TEXT_INPUT_FOCUS_STYLE : TEXT_INPUT_IDLE_STYLE),
            color: "#e2e8f0",
          }}
        />
        <p aria-live="polite" style={{ marginTop: "0.125rem", textAlign: "right", fontSize: "0.75rem", color: company.length >= COMPANY_MAX - 10 ? "#ef4444" : "#94a3b8" }}>
          {funnelText(content, "contactCompanyCounter", "{count}/{max}", { count: company.length, max: COMPANY_MAX })}
        </p>
      </div>

      <input type="text" name="website" style={{ display: "none" }} tabIndex={-1} aria-hidden />

      <div style={{ marginTop: "1.5rem" }}>
        <label htmlFor="privacy-consent" className="flex items-start gap-2 cursor-pointer" style={{ minHeight: "44px", paddingTop: "0.25rem", paddingBottom: "0.25rem" }}>
          <span style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px", flexShrink: 0 }}>
            <input
              ref={privacyInputRef}
              id="privacy-consent"
              type="checkbox"
              checked={privacy}
              onChange={(e) => {
                setPrivacy(e.target.checked);
                if (e.target.checked) {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.privacy;
                    return next;
                  });
                }
              }}
              disabled={submitting}
              aria-describedby={errors.privacy ? "fp-privacy-error" : undefined}
              style={{
                width: "1rem",
                height: "1rem",
                accentColor: "#2563eb",
                cursor: "pointer",
              }}
            />
          </span>
          <span style={{ fontSize: "0.875rem", color: "#94a3b8", lineHeight: "1.625" }}>
            {funnelText(content, "contactPrivacyConsent", "I agree that SEOH may use my planner details to save the handoff and generate the PDF. Planner context is kept for 30 days. No email is sent automatically.")}{" "}
            <a
              href={localizedPath(locale, "/privacy")}
              target="_blank"
              rel="noopener"
              style={{ textDecoration: "underline", color: "#94a3b8", fontSize: "0.875rem", minHeight: "44px", display: "inline" }}
            >
              {content.privacyLinkText}
            </a>
            {" "}<span aria-hidden="true" style={{ color: "#ef4444" }}>*</span>
          </span>
        </label>
        {errors.privacy && (
          <p
            id="fp-privacy-error"
            role="alert"
            tabIndex={-1}
            style={{
              marginTop: "0.25rem",
              fontSize: "0.875rem",
              color: "#ef4444",
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              paddingLeft: "0.5rem",
              borderLeft: "2px solid #ef4444",
              outline: "none",
              animation: "errorFadeIn 0.15s ease-out",
            }}
          >
            {errors.privacy}
          </p>
        )}
      </div>

      {/*
        Marketing consent has its own unticked, entirely optional control. It is
        never derived from the privacy agreement above, is not part of `validate`,
        and defaults to false, so submitting without touching it opts the visitor
        out rather than in.
      */}
      <div style={{ marginTop: "0.75rem" }}>
        <label htmlFor="marketing-consent" className="flex items-start gap-2 cursor-pointer" style={{ minHeight: "44px", paddingTop: "0.25rem", paddingBottom: "0.25rem" }}>
          <span style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", minWidth: "44px", minHeight: "44px", flexShrink: 0 }}>
            <input
              id="marketing-consent"
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              disabled={submitting}
              style={{
                width: "1rem",
                height: "1rem",
                accentColor: "#2563eb",
                cursor: "pointer",
              }}
            />
          </span>
          <span style={{ fontSize: "0.875rem", color: "#94a3b8", lineHeight: "1.625" }}>
            {funnelText(content, "contactMarketingOptIn", "Optional: email me occasional SEO tips and service updates. This is separate from the agreement above, is never required to get your plan, and you can unsubscribe at any time.")}
          </span>
        </label>
      </div>

      {errors.form && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
            borderRadius: "0.375rem",
            border: "1px solid rgba(239,68,68,0.4)",
            backgroundColor: "rgba(239,68,68,0.1)",
            padding: "0.75rem",
            animation: "errorFadeIn 0.15s ease-out",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem" }}>
            <span aria-hidden="true" style={{ color: "#ef4444", flexShrink: 0, fontSize: "1rem" }}>&#9888;</span>
            <p id="fp-form-error" role="alert" style={{ fontSize: "0.875rem", color: "#ef4444", margin: 0 }}>
              {errors.form}{" "}
              <Link href={localizedPath(locale, "/contact")} style={{ color: "#ef4444", textDecoration: "underline" }}>
                {funnelText(content, "contactSupportLink", "contact support")}
              </Link>.
            </p>
          </div>
          <button
            type="button"
            onClick={handleRetry}
            style={{
              alignSelf: "flex-start",
              padding: "0.375rem 0.875rem",
              fontSize: "0.8125rem",
              fontWeight: 600,
              color: "#ef4444",
              background: "transparent",
              border: "1px solid rgba(239,68,68,0.5)",
              borderRadius: "0.375rem",
              cursor: "pointer",
              minHeight: "44px",
            }}
          >
            {funnelText(content, "tryAgainButton", "Try again")}
          </button>
        </div>
      )}

      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {submitting ? progressPhases[progressPhase] : ""}
      </div>

      {submitting && (
        <div
          aria-hidden="true"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "0.8125rem",
            color: "#94a3b8",
            animation: "errorFadeIn 0.15s ease-out",
          }}
        >
          <svg
            style={{ width: "0.875rem", height: "0.875rem", animation: "spin 1s linear infinite", flexShrink: 0 }}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          {progressPhases[progressPhase]}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        aria-label={submitting ? funnelText(content, "contactProcessingButton", "Processing your plan...") : content.getMyPlanButton}
        aria-busy={submitting}
        aria-describedby={errors.form ? "fp-form-error" : undefined}
        data-analytics-component-family="cta"
        data-analytics-component-id="funnel-planner.contact-capture.cta.get-plan"
        data-analytics-cta-id="cta.funnel-planner.contact-capture.get-plan"
        data-analytics-experience-id="xp.funnel-planner.contact-capture.control"
        data-analytics-variant-key={state.templateId ?? "control"}
        data-funnel-planner-offer-assembly-cta="true"
        data-funnel-planner-step-seven-offer-assembly-cta="true"
        data-audit-ticket={joinAuditTicketGroups(STEP_SEVEN_CONTACT_CTA_TICKETS)}
        data-tier-one-family="offer_assembly_mini_cta_brief"
        data-gamification-current-state={submitting ? "active_inprogress" : downloadReviewState}
        data-gamification-reduced-motion-static="supported"
        data-gamification-supported-states={READINESS_SUPPORTED_STATES}
        data-readiness-ready-count={downloadReviewReadyCount}
        data-readiness-total-count={3}
        data-readiness-tier={downloadReviewReadyCount >= 3 ? "handoff_ready" : "handoff_draft"}
        data-blockers-bucket={downloadReviewReadyCount >= 3 ? "none" : downloadReviewReadyCount >= 1 ? "one_two" : "three_four"}
        onFocus={() => setSubmitFocused(true)}
        onBlur={() => setSubmitFocused(false)}
        style={{
          width: "100%",
          borderRadius: "0.375rem",
          backgroundColor: submitting ? "#1d4ed8" : "#2563eb",
          padding: "0.75rem 1.5rem",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "#ffffff",
          border: "none",
          cursor: submitting ? "not-allowed" : "pointer",
          opacity: submitting ? 0.7 : 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.5rem",
          minHeight: "44px",
          outline: submitFocused ? "2px solid var(--color-primary, #2563eb)" : "none",
          outlineOffset: "2px",
          transition: "background-color 0.15s ease",
        }}
      >
        {submitting ? (
          <svg
            aria-hidden="true"
            style={{ width: "1rem", height: "1rem", animation: "spin 1s linear infinite", flexShrink: 0 }}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
        ) : (
          <svg
            aria-hidden="true"
            style={{ width: "1rem", height: "1rem", flexShrink: 0 }}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        )}
        {submitting ? funnelText(content, "contactProcessingButton", "Processing your plan...") : content.getMyPlanButton}
        <span className="sr-only" role="status" aria-live="polite">
          {submitting
            ? progressPhases[progressPhase]
            : downloadReviewReadyCount >= 3
              ? funnelText(content, "gamificationDownloadReviewReady", "Download review ready")
              : funnelText(content, "gamificationDownloadReviewWaiting", "Download review waiting for contact consent")}
        </span>
      </button>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes errorFadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </form>
  );
}
